import { useCallback, useEffect, useState } from "react";

import { businessApi } from "../../../../services/api";
import { Button, EmptyState, ErrorState, Field, Input, Select, SkeletonRows, Surface } from "../../../../components/ui";
import RequestDecisionDialog from "./RequestDecisionDialog";
import {
  DISPOSITION_CHOICE, DISPOSITION_HINT, DISPOSITION_LABEL, REFERRAL_NEXT_STEP, REVIEW_KIND_LABEL, correctionLine, describeReviewItem, reviewNextStep,
} from "../scheduledOvertimeText";
import { formatDay, formatMinutes } from "../myHrText";

// Report 70 (APP-HR-149, owner decision D7): overtime days payroll cannot settle on its own. Approved overtime with no
// clock-out is not paid until the clock-out is corrected or HR records what happened; time worked after the shift
// without approval is kept and needs a reasoned decision. Missing approval alone never proves the time is not owed, so
// "Not overtime" needs what it relies on. Each open day holds that employee's payroll, like a pending request.

function ReviewRow({ item, canDecide, onDecide }) {
  return (
    <li className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 space-y-0.5">
        <p className="text-body-sm font-medium text-foreground">{item.memberName || "Employee"}</p>
        <p className="text-body-sm text-foreground">{describeReviewItem(item)}</p>
        {item.review ? (
          <p className="text-meta text-secondary">
            {DISPOSITION_LABEL[item.review.disposition]}
            {item.review.correction?.number ? ` ${item.review.correction.number}` : ""}: {item.review.reason}
            {item.review.evidenceRef ? ` · relies on: ${item.review.evidenceRef}` : ""}
            {item.review.reviewerName ? ` · ${item.review.reviewerName}` : ""}
          </p>
        ) : null}
        {item.review?.correction ? <p className="text-meta text-foreground">{correctionLine(item.review)}</p> : null}
        {!item.review ? <p className="text-meta text-secondary">{reviewNextStep(item)}</p> : null}
        {item.state === "referred" && item.review?.disposition === "REFERRED" ? <p className="text-meta text-foreground">{REFERRAL_NEXT_STEP}</p> : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
        <span className={`text-body-sm ${item.state === "reviewed" ? "text-secondary" : "text-warning"}`}>
          {{ open: "Needs review", referred: "Referred · follow up" }[item.state] || "Reviewed"}
        </span>
        {canDecide ? (
          <Button size="sm" variant={item.state === "reviewed" ? "quiet" : "secondary"} onClick={() => onDecide(item)}>
            {item.state === "referred" && (item.review?.disposition === "REFERRED" || item.review?.correction?.stage === "void") ? "Record outcome" : item.review ? "Change" : "Record decision"}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

export default function OvertimeReviewPanel({ canDecide = false, reloadKey = 0, onNotice, onOpenCount }) {
  const [state, setState] = useState("open");
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [disposition, setDisposition] = useState("");
  const [evidence, setEvidence] = useState("");
  const [correction, setCorrection] = useState("");
  const [settledMinutes, setSettledMinutes] = useState("");
  const [localReload, setLocalReload] = useState(0);

  const load = useCallback(async (signal) => {
    try {
      const res = await businessApi.getOvertimeReviewItems({ state }, { signal });
      setResult(res);
      setFailed(false);
      if (state === "open") onOpenCount?.((res?.items || []).length);
    } catch {
      if (!signal?.aborted) setFailed(true);
    }
  }, [state, onOpenCount]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, reloadKey, localReload]);

  const startDecision = (item) => {
    // A referral's follow-up starts on its first final outcome, never on "Referred" again.
    setDisposition(item.state === "referred" ? item.dispositions[0] : item.review?.disposition || item.dispositions[0]);
    setEvidence(item.review?.evidenceRef || "");
    setCorrection(item.review?.correction?.number || "");
    setSettledMinutes(String(item.review?.correction?.settledMinutes || item.minutes || ""));
    setDeciding(item);
  };

  const submit = async ({ note, overrideReason }) => {
    if (disposition === "NOT_OVERTIME" && !evidence.trim()) {
      const error = new Error("Say what this relies on.");
      error.serverMessage = "Say what this relies on (for example the employee's message or a note from their supervisor).";
      throw error;
    }
    if (disposition === "SETTLED_BY_CORRECTION" && !correction.trim()) {
      const error = new Error("Name the pay correction.");
      error.serverMessage = "Enter the number of the pay correction that pays it (Payroll › Corrections).";
      throw error;
    }
    const minutesOwed = Number(settledMinutes);
    if (disposition === "SETTLED_BY_CORRECTION" && (!Number.isInteger(minutesOwed) || minutesOwed < 1 || minutesOwed > deciding.minutes)) {
      const error = new Error("Minutes owed.");
      error.serverMessage = `Enter the minutes found owed: a whole number from 1 to ${deciding.minutes}.`;
      throw error;
    }
    await businessApi.recordOvertimeReview({
      attendance_record_id: deciding.attendanceRecordId,
      kind: deciding.kind,
      disposition,
      reason: note,
      evidence_ref: evidence.trim() || undefined,
      ...(disposition === "SETTLED_BY_CORRECTION" ? { correction_number: correction.trim(), settled_minutes: minutesOwed } : {}),
      ...(overrideReason ? { override_reason: overrideReason } : {}),
    });
    setDeciding(null);
    setLocalReload((key) => key + 1);
    onNotice?.(disposition === "SETTLED_BY_CORRECTION"
      ? `Pay correction ${correction.trim()} linked for ${deciding.memberName || "the employee"} on ${formatDay(deciding.date, { year: true })}. The day stays under "Referred · follow up" until Finance records that correction as paid.`
      : `${DISPOSITION_LABEL[disposition]} recorded for ${deciding.memberName || "the employee"} on ${formatDay(deciding.date, { year: true })}. The clock times stay on record.`);
  };

  const items = result?.items || [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-[90ch] text-body-sm text-secondary">
          {state === "referred"
            ? "Referred days no longer hold payroll but are not settled. Every open referral is listed, however old, until you record its outcome."
            : "Days payroll cannot settle on its own (the last 30 days). Each one waiting here holds that employee's payroll until it is corrected or decided."}
        </p>
        <Field label="Show" className="w-48">
          <Select value={state} onChange={(e) => setState(e.target.value)}>
            <option value="open">Needs review</option>
            <option value="referred">Referred · follow up</option>
            <option value="reviewed">Reviewed</option>
            <option value="all">All</option>
          </Select>
        </Field>
      </div>
      {failed ? <ErrorState title="Overtime to review could not be loaded" onRetry={() => setLocalReload((key) => key + 1)} /> : null}
      {!failed && !result ? <Surface><SkeletonRows rows={3} /></Surface> : null}
      {!failed && result && !items.length ? (
        <Surface>
          <EmptyState title={state === "open" ? "Nothing needs review" : "No overtime reviews"} description={state === "open" ? "Approved overtime without a clock-out, and time worked after a shift without approval, appear here." : "Decisions recorded on overtime days appear here."} />
        </Surface>
      ) : null}
      {items.length ? (
        <Surface className="p-0">
          <ul className="divide-y divide-subtle">
            {items.map((item) => <ReviewRow key={`${item.attendanceRecordId}-${item.kind}`} item={item} canDecide={canDecide} onDecide={startDecision} />)}
          </ul>
        </Surface>
      ) : null}
      {deciding ? (
        <RequestDecisionDialog
          title={deciding.state === "referred" ? `Record the outcome of ${deciding.memberName || "this employee"}'s referred overtime?` : `Record a decision on ${deciding.memberName || "this employee"}'s overtime?`}
          facts={[
            { label: "Employee", value: deciding.memberName || "Employee" },
            { label: "Day", value: formatDay(deciding.date, { year: true }) },
            { label: "What happened", value: `${REVIEW_KIND_LABEL[deciding.kind]} (${formatMinutes(deciding.minutes)})` },
            ...(deciding.state === "referred" ? [{ label: "Referred because", value: deciding.review?.reason || "—" }] : []),
          ]}
          consequence={DISPOSITION_HINT[disposition]}
          confirmLabel={deciding.state === "referred" ? "Record outcome" : "Record decision"}
          reason={{ label: "What happened (the employee sees this)", required: true }}
          onSubmit={submit}
          onClose={() => setDeciding(null)}
        >
          <Field label="Decision" required>
            <Select value={disposition} onChange={(e) => setDisposition(e.target.value)}>
              {deciding.dispositions.map((option) => <option key={option} value={option}>{DISPOSITION_CHOICE[option]}</option>)}
            </Select>
          </Field>
          {disposition === "SETTLED_BY_CORRECTION" ? (
            <>
              <Field label="Minutes found owed" required hint={`Up to the ${deciding.minutes} min in question.`}>
                <Input type="number" inputMode="numeric" min={1} max={deciding.minutes} step={1} value={settledMinutes} onChange={(e) => setSettledMinutes(e.target.value)} />
              </Field>
              <Field label="Pay correction number" required hint="From Payroll › Corrections: an underpayment raised as added overtime minutes on this period's payslip.">
                <Input maxLength={32} value={correction} onChange={(e) => setCorrection(e.target.value)} placeholder="For example: ADJ-2026-0012" />
              </Field>
            </>
          ) : null}
          {disposition === "NOT_OVERTIME" || evidence ? (
            <Field label="What it relies on" required={disposition === "NOT_OVERTIME"} hint="A message, a supervisor's note, a document. Missing approval alone is not enough.">
              <Input maxLength={255} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
            </Field>
          ) : null}
        </RequestDecisionDialog>
      ) : null}
    </div>
  );
}
